import React, { useMemo } from 'react';
import { Spin } from 'antd';
import moment from 'moment';
import clsx from 'clsx';
import { ExportToCsv } from 'export-to-csv';
import DeltaArrowIcon from '../icons/DeltaArrowIcon';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import {
	CHART_PRIMARY_COLOR,
	COMPARISON_PERIOD_COLOR,
	NEGATIVE_CHANGE_COLOR,
	POSITIVE_CHANGE_COLOR,
} from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsConstants';
import {
	formatDurationShort,
	parseSafeFloat,
	roundToOneDecimalPlace,
} from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsUtils';
import { buildSeriesComparisonTooltipHtml } from '../../utils/AnalyticsComparisonTooltipUtils';
import {
	COMPARISON_MODE,
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
	getAnalyticsRolePrimaryColor,
} from '../../utils/AnalyticsComparisonUtils';
import ComparisonLegend from '../analytics_comparison/ComparisonLegend';
import {
	AnalyticsChartActions,
	AnalyticsChartCard,
	AnalyticsChartHeader,
	MetricLineChart,
	pairComparisonByMonth,
} from '../analytics_charts';
import { useAnalyticsBreakdownComparison } from '../../hooks/useAnalyticsBreakdownComparison';
import { fetchKPIComparisonSilent } from '../../thunks/kpi_comparison_thunks';
import { EmptyState } from '../empty_state/EmptyState';

export type KPIMetricChartProps = {
	/** Used as a stable identifier for the chart (data fetch + tooltip keys). */
	chartKey: string;
	label: string;
	/** Lifetime / aggregate metric value to render in the header. Numeric in raw API units. */
	summaryValue: number | null | undefined;
	/** Previous-period aggregate (used to draw the up/down delta). */
	previousSummaryValue?: number | null;
	data: any[];
	loading: boolean;
	/** True when the metric is a duration (seconds), false when it is a percentage 0..1. */
	isTimeBased: boolean;
	/** SLA baseline shown as a horizontal reference (currently unused — kept for future Geom). */
	baseline?: number;
	/**
	 * KPI endpoint substring used for both the current and the comparison fetch. The endpoint
	 * mirrors `KPI_COMPARISON_API_SUBSTRINGS` in `kpi_comparison_thunks.ts`.
	 */
	comparisonApiSubstring: string;
	/** Filters applied to the page (start/end date, supplier, problem types, locations). */
	filters: Record<string, any>;
	userType: string;
};

const PERCENT_DISPLAY_AXIS_TITLE = 'Percentage';
const TIME_DISPLAY_AXIS_TITLE = 'Duration';

const toMonthRow = (row: any, isTimeBased: boolean) => {
	const yearMonthKey = (row && row.yearMonthKey) || '';
	const [yearStr, monthStr] = yearMonthKey.split('-');
	const monthIndex = Math.max(0, parseInt(monthStr || '1', 10) - 1);
	const yearNumber = parseInt(yearStr || '0', 10);
	const date = moment({ year: yearNumber, month: monthIndex });
	const rawAverage = parseSafeFloat(row && row.monthlyAverage, 0);
	return {
		...row,
		monthKey: `${yearNumber}-${monthIndex + 1}`,
		// Short month label on the axis (e.g. "May '25") matches the lifecycle pattern and
		// keeps wide x-axis ticks tidy. `monthFull` stays long-form for tooltip headers.
		monthLabel: date.format("MMM [']YY"),
		monthFull: date.format('MMMM YYYY'),
		metricRaw: rawAverage,
		// For percent metrics raw is 0..1; multiply for axis/value display.
		metricValue: isTimeBased ? rawAverage : rawAverage * 100,
	};
};

const KPIMetricChart = ({
	chartKey,
	label,
	summaryValue,
	previousSummaryValue,
	data,
	loading,
	isTimeBased,
	comparisonApiSubstring,
	filters,
	userType,
}: KPIMetricChartProps) => {
	const currentColor = getAnalyticsRolePrimaryColor(userType) || CHART_PRIMARY_COLOR;
	const previousColor = COMPARISON_PERIOD_COLOR;

	const {
		comparisonState,
		setComparison,
		comparisonRows,
		comparisonLoading,
		selectedStart,
		selectedEnd,
		comparisonRange,
		comparisonDisabled,
		comparisonDisabledReason,
		hasComparison,
	} = useAnalyticsBreakdownComparison({
		filters,
		fetchComparisonData: fetchKPIComparisonSilent(comparisonApiSubstring)(userType),
	});

	const currentRows = useMemo(
		() => (data || []).map((row) => toMonthRow(row, isTimeBased)),
		[data, isTimeBased]
	);
	const previousRows = useMemo(
		() => (comparisonRows || []).map((row) => toMonthRow(row, isTimeBased)),
		[comparisonRows, isTimeBased]
	);

	const chartData = useMemo(() => {
		const paired = pairComparisonByMonth({
			current: currentRows,
			previous: previousRows,
			hasComparison,
			selectedRange: selectedStart && selectedEnd ? [selectedStart, selectedEnd] : null,
			comparisonRange,
			buildPreviousRow: (prevRow, curRow: any) => ({
				...(prevRow || {}),
				monthLabel: curRow.monthLabel,
			}),
		});
		return [...paired.current, ...paired.previous];
	}, [currentRows, previousRows, hasComparison, selectedStart, selectedEnd, comparisonRange]);

	const formatYAxisLabel = (val: any) => {
		const num = parseSafeFloat(val, NaN);
		if (isNaN(num)) return '';
		if (isTimeBased) return formatDurationShort(num);
		return `${roundToOneDecimalPlace(num)}%`;
	};

	const formatSummary = (raw: number | null | undefined) => {
		if (raw === null || raw === undefined) return '—';
		if (isTimeBased) return formatDurationShort(raw);
		return `${roundToOneDecimalPlace(raw * 100)}%`;
	};

	const summaryFormatted = formatSummary(summaryValue);

	// The KPI vendor page renders aggregate headers but doesn't fetch a previous-period aggregate,
	// so when an explicit `previousSummaryValue` isn't supplied we derive it from the comparison
	// rows the chart already loads — averaging the monthly raw values the same way `summaryValue`
	// represents an average metric, so the header delta stays comparable.
	const derivedPreviousSummary = useMemo(() => {
		if (previousSummaryValue !== null && previousSummaryValue !== undefined) {
			return previousSummaryValue;
		}
		if (!previousRows || previousRows.length === 0) return null;
		const values = previousRows
			.map((row) => parseSafeFloat(row.metricRaw, NaN))
			.filter((num) => !isNaN(num));
		if (values.length === 0) return null;
		const total = values.reduce((sum, num) => sum + num, 0);
		return total / values.length;
	}, [previousSummaryValue, previousRows]);

	const deltaElement = useMemo(() => {
		if (
			!hasComparison ||
			summaryValue === null ||
			summaryValue === undefined ||
			derivedPreviousSummary === null ||
			derivedPreviousSummary === undefined
		) {
			return null;
		}
		if (!derivedPreviousSummary) return null;
		const pct = ((summaryValue - derivedPreviousSummary) / derivedPreviousSummary) * 100;
		if (!isFinite(pct) || pct === 0) return null;
		const isUp = pct > 0;
		// For metrics we want UP to be good — true for percentage KPIs; for time-based metrics a
		// shorter duration is better, so flip the color semantics.
		const isPositiveOutcome = isTimeBased ? !isUp : isUp;
		const color = isPositiveOutcome ? NEGATIVE_CHANGE_COLOR : POSITIVE_CHANGE_COLOR;
		return (
			<div
				className={clsx(
					'flex shrink-0 items-center gap-1',
					isPositiveOutcome ? 'text-lifecycleChange-negative' : 'text-lifecycleChange-positive'
				)}
			>
				<DeltaArrowIcon direction={isUp ? 'up' : 'down'} color={color} />
				<span className="bodyregular">{Math.abs(roundToOneDecimalPlace(pct))}%</span>
			</div>
		);
	}, [hasComparison, summaryValue, derivedPreviousSummary, isTimeBased]);

	const seriesColorMap: Record<string, string> = {
		[COMPARISON_SERIES_CURRENT]: currentColor,
		[COMPARISON_SERIES_PREVIOUS]: previousColor,
	};

	const isCurrentItem = (item: any) =>
		!!(item && item.data && item.data[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_CURRENT);

	const renderComparisonTooltip = (title: string, items: any[]) => {
		if (!items || items.length === 0) return '';
		const rows = items
			.slice()
			.sort((a, b) => Number(isCurrentItem(b)) - Number(isCurrentItem(a)))
			.map((item) => {
				const monthFull = (item.data && item.data.monthFull) || title;
				const raw = parseSafeFloat(item.data && item.data.metricRaw, NaN);
				const formatted = isNaN(raw)
					? '-'
					: isTimeBased
					? formatDurationShort(raw)
					: `${roundToOneDecimalPlace(raw * 100)}%`;
				return {
					label: monthFull,
					color: seriesColorMap[item.data && item.data[COMPARISON_SERIES_FIELD]] || currentColor,
					value: formatted,
					dashed: !isCurrentItem(item),
				};
			});
		return buildSeriesComparisonTooltipHtml(rows);
	};

	const pointTooltipDefinition: [string, (...args: any[]) => { name: string; value: string }] = [
		'monthFull*metricValue*metricRaw',
		(monthFull: any, metricVal: any, metricRaw: any) => {
			const raw = parseSafeFloat(metricRaw, NaN);
			const formatted = isNaN(raw)
				? '-'
				: isTimeBased
				? formatDurationShort(raw)
				: `${roundToOneDecimalPlace(parseSafeFloat(metricVal))}%`;
			return {
				name: label,
				value: `<div class=\"flex flex-col gap-1 px-2 py-1 text-xs\"><span class=\"font-medium\">${monthFull}</span><span>${label}: ${formatted}</span></div>`,
			};
		},
	];

	const comparisonStart: moment.Moment | null = comparisonRange ? comparisonRange[0] : null;
	const comparisonEnd: moment.Moment | null = comparisonRange ? comparisonRange[1] : null;

	const title = (
		<AnalyticsChartHeader label={label} value={summaryFormatted} delta={deltaElement} />
	);

	const formatExportValue = (raw: number | null | undefined) => {
		if (raw === null || raw === undefined) return '';
		const num = parseSafeFloat(raw, NaN);
		if (isNaN(num)) return '';
		return isTimeBased ? formatDurationShort(num) : `${roundToOneDecimalPlace(num * 100)}%`;
	};

	const handleDownload = () => {
		if (!currentRows || currentRows.length === 0) return;
		const includeComparison = hasComparison && previousRows.length > 0;
		const baseHeaders = ['Month', label];
		const headers = includeComparison ? [...baseHeaders, 'Historical Data'] : baseHeaders;

		const mapRow = (row: any, isHistorical: boolean): Record<string, string | number> => {
			const base: Record<string, string | number> = {
				month: row.monthFull || row.monthLabel || '',
				value: formatExportValue(row.metricRaw),
			};
			if (includeComparison) {
				// Match the lifecycle/breakdown CSV pattern: "Yes" for previous-period rows so the
				// reader can filter or pivot historical vs current.
				base.historicalData = isHistorical ? 'Yes' : 'No';
			}
			return base;
		};

		const previousExportRows = includeComparison
			? previousRows.map((row: any) => mapRow(row, true))
			: [];
		const currentExportRows = currentRows.map((row: any) => mapRow(row, false));

		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: label,
			title: label,
			headers,
		});
		csvExporter.generateCsv([...previousExportRows, ...currentExportRows]);
	};

	const extra = (
		<AnalyticsChartActions
			comparison={{
				value: comparisonState || { mode: COMPARISON_MODE.NONE },
				onChange: setComparison,
				selectedStart,
				selectedEnd,
				isTimeBased: true,
				loading: !!comparisonLoading,
				disabled: !!comparisonDisabled,
				disabledReason: comparisonDisabled ? comparisonDisabledReason : undefined,
			}}
			onDownload={handleDownload}
		/>
	);

	// When the supplier / date range has no KPI rows, show the "data not available" state instead
	// of an empty chart shell (which reads as a broken chart). Mirrors the previous vendor page.
	const hasData = currentRows.length > 0;

	return (
		<AnalyticsChartCard title={title} extra={extra}>
			<Spin spinning={loading || comparisonLoading}>
				{!hasData && !loading && !comparisonLoading ? (
					<EmptyState
						height={300}
						headline={'KPI data not available'}
						body={<div style={{ maxWidth: 488, textAlign: 'center' }}></div>}
					/>
				) : (
					<div className="w-full min-w-0">
						<MetricLineChart
							data={chartData}
							hasComparison={hasComparison}
							currentColor={currentColor}
							previousColor={previousColor}
							axisYTitle={isTimeBased ? TIME_DISPLAY_AXIS_TITLE : PERCENT_DISPLAY_AXIS_TITLE}
							formatYAxisLabel={formatYAxisLabel}
							pointTooltipDefinition={pointTooltipDefinition}
							renderComparisonTooltip={renderComparisonTooltip}
							height={300}
						/>
						{hasComparison ? (
							<ComparisonLegend
								currentStart={selectedStart}
								currentEnd={selectedEnd}
								previousStart={comparisonStart}
								previousEnd={comparisonEnd}
								currentColor={currentColor}
								previousColor={previousColor}
								mode={comparisonState.mode}
							/>
						) : (
							<div className="mt-3 h-5" aria-hidden="true" />
						)}
					</div>
				)}
			</Spin>
		</AnalyticsChartCard>
	);
};

export default KPIMetricChart;
